import "./play-error-notice.less"
import { useEffect, useRef } from "react"
import { useMessages } from "play/language/language"
import { usePlayState } from "play/hooks/use-play-state"
import { clearPlayErrorReducer } from "play/reducers"
import {
  buildErrorReport,
  queueErrorReport,
  sendQueuedErrorReports,
  shownGameCode,
} from "play/error-report"
import { setGlobalDontPreventBrowserClose } from "play/play-model"

/**
 * Tells the player that a server request failed, in three strengths:
 *
 * - the message poll is being retried: a quiet "trying again"
 * - one action failed (`playError.level === "action"`): what did not happen,
 *   and that they can try again
 * - the server has the answer already and this page is behind (`"behind"`):
 *   reload the page, or close the message
 * - the connection is gone (`"fatal"`): reload the page
 *
 * The text never says what went wrong technically. The last line (game code,
 * time, code) is what support needs: the failure is reported to the server
 * under that code, see error-report.ts.
 */
export default function PlayErrorNotice() {
  const state = usePlayState()
  const { playError, isReconnecting, isTeacherUrl, gameCode, modifyState } =
    state
  const msg = useMessages()

  // reports a previous page could not send (it lost the connection, the
  // player reloaded)
  useEffect(() => {
    sendQueuedErrorReports()
  }, [])

  const stateRef = useRef(state)
  stateRef.current = state

  useEffect(() => {
    if (!playError) return
    queueErrorReport(buildErrorReport(playError, stateRef.current))
  }, [playError?.code, playError?.time])

  if (playError) {
    const isFatal = playError.level === "fatal"
    const needsReload = isFatal || playError.level === "behind"
    const reference = [
      shownGameCode(gameCode),
      playError.shownTime,
      playError.code,
    ]
      .filter((x) => !!x)
      .join(" · ")

    return (
      <div
        className="play-error-notice"
        role="alert"
        aria-live="assertive"
        data-error-code={playError.code}
      >
        <div>
          <p className="message">{msg.playErrorToString(playError)}</p>
          {needsReload && (
            <a
              className="reload"
              href={location.href}
              onClick={(e) => {
                e.preventDefault()
                setGlobalDontPreventBrowserClose(true)
                location.reload()
              }}
            >
              {msg.connectionLostRefreshPage}
            </a>
          )}
          {isTeacherUrl && <p className="hint">{msg.playError_sendScreenshot}</p>}
          <p className="reference">{reference}</p>
        </div>
        {!isFatal && (
          <button
            type="button"
            className="btn-close"
            aria-label={msg.dlgClose}
            onClick={() => modifyState(clearPlayErrorReducer)}
          />
        )}
      </div>
    )
  }

  if (isReconnecting) {
    return (
      <div className="play-error-notice reconnecting" role="status">
        {msg.playError_reconnecting}
      </div>
    )
  }

  return null
}
